/* eslint-disable sonarjs/no-duplicate-string */
import {
  Prisma,
  Action as PrismaActionType,
  RightEntity,
  RightsRole as RightsRolePrisma,
} from "@prisma/client";

export const actionValues = [
  "read",
  "create",
  "update",
  "delete",
  "manage",
  "export",
  "iaAccess",
] as const;

export type Action = (typeof actionValues)[number];

export type RightRole = Pick<RightsRolePrisma, "id" | "name">;

export type MainSection =
  | "statistics"
  | "datas"
  | "event"
  | "conversation"
  | "userAccess";

type RightDependency = {
  right: RightEntity;
  action: PrismaActionType;
};

export type RightSection = {
  label: string;
  rights: {
    value: RightEntity;
    name: string;
    options: PrismaActionType[];
    dependsOn?: RightDependency[];
    dependsOnAny?: RightDependency[];
  }[];
};

type Right = {
  [K in MainSection]: RightSection;
};

export const rightsList: Right = {
  statistics: {
    label: "Statistiques",
    rights: [
      {
        value: "statistics",
        name: "Statistiques",
        options: ["read"],
      },
    ],
  },
  datas: {
    label: "Mes clients",
    rights: [
      {
        value: "clientExhibitor",
        name: "Exposants",
        options: ["read", "create", "export"],
      },
      {
        value: "clientVisitor",
        name: "Visiteurs",
        options: ["read", "create", "export"],
      },
      {
        value: "clientGroup",
        name: "Groupes",
        options: ["read", "create", "update", "delete", "export"],
        dependsOnAny: [
          { right: "clientVisitor", action: "read" },
          { right: "clientVisitor", action: "create" },
          { right: "clientVisitor", action: "export" },
          { right: "clientExhibitor", action: "read" },
          { right: "clientExhibitor", action: "create" },
          { right: "clientExhibitor", action: "export" },
        ],
      },
    ],
  },
  event: {
    label: "Événement",
    rights: [
      {
        value: "eventMain",
        name: "Fiche d'information",
        options: ["read", "create", "update", "delete", "iaAccess"],
      },
      {
        value: "eventTicketing",
        name: "Billetterie",
        options: ["read", "create", "update", "delete", "export"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventCashRegister",
        name: "Guichets",
        options: ["read", "create", "update", "delete", "export"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventAccessControl",
        name: "Contrôle d'accès",
        options: ["read", "create", "update", "delete"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventExhibitor",
        name: "Exposants",
        options: ["read", "create", "update", "delete"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventBadge",
        name: "Badges",
        options: ["read", "create", "update", "delete"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventInvitation",
        name: "Invitations",
        options: ["read", "create"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventSchedule",
        name: "Programme",
        options: ["read", "create", "update", "delete"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventFunctionality",
        name: "Gérer les fonctionnalités",
        options: ["read", "update"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventTransfer",
        name: "Transférer un événement",
        options: ["update"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
      {
        value: "eventCommunicationKit",
        name: "Kit de communication",
        options: ["read", "create", "update", "delete"],
        dependsOn: [{ right: "eventMain", action: "read" }],
      },
    ],
  },
  conversation: {
    label: "Conversation",
    rights: [
      {
        value: "conversation",
        name: "Conversation",
        options: ["read", "create"],
      },
    ],
  },
  userAccess: {
    label: "Gestion des accès",
    rights: [
      {
        value: "userAccess",
        name: "Gestion des accès",
        options: ["read", "create", "update", "delete"],
      },
    ],
  },
} as const;

export type UpsertRightRoleDto = {
  name: string;
  rightRoleId?: string;
  acAcExpo?: boolean;
};

const rightsRoleValues = Prisma.validator<Prisma.RightsRoleDefaultArgs>()({
  include: {
    rights: true,
  },
});

export type RightsRole = Prisma.RightsRoleGetPayload<typeof rightsRoleValues>;

export type AcExpoMainSection =
  | "dashboard"
  | "event"
  | "data"
  | "conversation"
  | "userAccess"
  | "notification"
  | "settings"
  | "remoteControl"
  | "reporting";

type AcExpoRight = {
  [K in AcExpoMainSection]: RightSection;
};

export const acExpoRightsList: AcExpoRight = {
  dashboard: {
    label: "Tableau de bord & Utilisateurs",
    rights: [
      {
        value: "acExpoDashboard",
        name: "Tableau de bord",
        options: ["read"],
      },
      {
        value: "acExpoUser",
        name: "Utilisateurs",
        options: ["read"],
      },
    ],
  },
  event: {
    label: "Événements",
    rights: [
      {
        value: "acExpoAllEvent",
        name: "Tous les événements",
        options: ["read", "update"],
      },
      {
        value: "acExpoEventBeforeOnlineControl",
        name: "Événements en attente de mise en ligne",
        options: ["read", "update", "delete"],
      },
      {
        value: "acExpoEventAfterVisitorCreationControl",
        name: "Événements visiteurs à contrôler",
        options: ["read", "update", "delete"],
      },
      {
        value: "acExpoEventReported",
        name: "Événements signalés",
        options: ["read", "update", "delete"],
      },
      {
        value: "acExpoEventAfterAiCreationControl",
        name: "Événements extraits automatiquement",
        options: ["read", "update", "delete"],
      },
    ],
  },
  data: {
    label: "Données",
    rights: [
      {
        value: "acExpoConnectEventsData",
        name: "Données Connect'Events",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingTicketing",
        name: "Données comptabilité - Billetterie",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingService",
        name: "Données comptabilité - Prestations",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingPurchase",
        name: "Données comptabilité - Achats",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingInvoice",
        name: "Données comptabilité - Factures",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingClient",
        name: "Données comptabilité - Clients",
        options: ["read", "export"],
      },
      {
        value: "acExpoAccountingCreditNote",
        name: "Données comptabilité - Avoirs Organisateurs",
        options: ["read", "create", "update", "delete"],
      },
    ],
  },
  conversation: {
    label: "Conversation",
    rights: [
      {
        value: "acExpoConversation",
        name: "Conversation",
        options: ["read", "create"],
      },
    ],
  },
  userAccess: {
    label: "Gestion des accès",
    rights: [
      {
        value: "acExpoUserAccess",
        name: "Gestion des accès",
        options: ["read", "create", "update", "delete"],
      },
    ],
  },
  notification: {
    label: "Notifications",
    rights: [
      {
        value: "acExpoNotification",
        name: "Notifications",
        options: ["read"],
      },
      {
        value: "acExpoActionEmailNotification",
        name: "Notifications des événements (mise en ligne, signalement, etc.) par email",
        options: ["read"],
      },
    ],
  },
  settings: {
    label: "Paramètres",
    rights: [
      {
        value: "acExpoSettingsStock",
        name: "Paramètres - Stocks",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsDiscountCode",
        name: "Paramètres - Codes promos",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsFaq",
        name: "Paramètres - FAQ",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsPrestation",
        name: "Paramètres - Prestations",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsPayments",
        name: "Paramètres - Gestion des paiements",
        options: ["read", "update"],
      },
      {
        value: "acExpoSettingsStats",
        name: "Paramètres - Gestion des stats",
        options: ["read"],
      },
      {
        value: "acExpoSettingsEventFormQuestions",
        name: "Paramètres - Formulaires événements",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsFaqChatbot",
        name: "Paramètres - FAQ Chatbot",
        options: ["read", "update", "create", "delete"],
      },
      {
        value: "acExpoSettingsBusinessSector",
        name: "Paramètres - Secteurs d'activité",
        options: ["read", "update", "create", "delete"],
      },
    ],
  },
  remoteControl: {
    label: "Contrôle à distance",
    rights: [
      {
        value: "acExpoRemoteControl",
        name: "Contrôle à distance",
        options: ["read"],
      },
    ],
  },
  reporting: {
    label: "Reporting",
    rights: [
      {
        value: "acExpoReporting",
        name: "Reporting",
        options: ["read"],
      },
    ],
  },
} as const;
